import axios, { AxiosResponse } from 'axios';
import { useMutation } from 'react-query';

import { IApprovedTimesheet } from 'features/pending-timesheets/models/approved-timesheet.model';
import { PENDING_TIMESHEETS } from 'api/use-query-constants';
import { PendingTimesheet } from 'features/pending-timesheets/models/pending-timesheets.model';
import {
    setChangedPendingTimesheets,
    setPendingTimesheets,
} from 'features/pending-timesheets/redux/pending-timesheets-slice';
import { useTrackCotapEvent } from '../../app-insights/track-event.hook';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/root-reducer';
import { AppConfig } from '../../config/config.model';
import { openModal } from '../../../redux/message-modal-slice';
import { getQueryClient } from '../../config/config';

export interface IApproveVariables {
    approvedTimesheets: IApprovedTimesheet[];
    updatedTimeheets: PendingTimesheet[];
    supervisorSignature: string;
    supervisorName: string;
    timesheets: PendingTimesheet[];
}

export interface IApproveMutationHook {
    mutate: (variables: IApproveVariables) => void;
    isApproveTimesheetingsLoading: boolean;
}

export const useApproveTimesheetsMutation = (url: string, onSuccess: () => void): IApproveMutationHook => {
    const trackError = useTrackCotapEvent('SupervisorApproval Error', false);
    const config = useSelector((state: RootState) => state.globalElements.appConfig as AppConfig);
    const isUsingV2Endpoints = useSelector((state: RootState) => state.globalElements.isUsingV2Endpoints);
    const dispatch = useDispatch();
    const queryClient = getQueryClient();

    const { mutate, isLoading: isApproveTimesheetingsLoading } = useMutation(
        ({ approvedTimesheets, supervisorSignature, supervisorName }: IApproveVariables) => {
            const timesheetIdArray = approvedTimesheets.map((timesheet: IApprovedTimesheet) => timesheet.id);

            if (isUsingV2Endpoints) {
                return axios
                    .post(`${config.TIMESHEET_SERVICE_BASE_URL}/TimesheetApproval/v2/SupervisorApproval`, {
                        cotaSecret: url,
                        supervisorApprovalParams: {
                            timesheetIds: timesheetIdArray,
                            supervisorSignature,
                            supervisorName,
                        },
                    })
                    .then((response: AxiosResponse<any>) => response?.data);
            }

            return (
                axios
                    .post(
                        `${config.TIMESHEET_SERVICE_BASE_URL}/TimesheetApproval/SupervisorApproval?clientOnlineTimesheetApprovalSecret=${url}`,
                        {
                            timesheetIdArray,
                            supervisorSignature,
                            supervisorName,
                        },
                    )
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .then((response: AxiosResponse<any>) => response?.data)
            );
        },
        {
            onMutate: ({ approvedTimesheets, updatedTimeheets }: IApproveVariables) => {
                //cancel any outgoing refetches
                queryClient.cancelQueries(PENDING_TIMESHEETS);

                //snapshot previous values
                const previousPendingTimesheets = queryClient.getQueryData(PENDING_TIMESHEETS);

                // Optimistically update to the new value
                queryClient.setQueryData(PENDING_TIMESHEETS, updatedTimeheets);

                const updated = updatedTimeheets.map((updatedTimesheet: PendingTimesheet) => {
                    if (
                        approvedTimesheets.some(
                            (approvedTs: IApprovedTimesheet) => approvedTs.id === updatedTimesheet.timesheetId,
                        )
                    ) {
                        return {
                            ...updatedTimesheet,
                            onlineApprovalDate: updatedTimesheet.onlineApprovalDate || new Date(),
                        };
                    }

                    return updatedTimesheet;
                });

                setPendingTimesheets(updated);
                setChangedPendingTimesheets(updated);

                onSuccess();
                return () => {
                    queryClient.setQueryData(PENDING_TIMESHEETS, previousPendingTimesheets);
                };
            },

            onSuccess,

            onError: (
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                err: any,
                variables: IApproveVariables,
                rollback: () => void,
            ) => {
                if (typeof rollback === 'function') {
                    rollback();
                }

                trackError({ err });

                setPendingTimesheets(variables.timesheets);
                setChangedPendingTimesheets(variables.timesheets);

                dispatch(
                    openModal({
                        title: 'Error submitting timesheet',
                        body: 'Please contact Medical Solutions',
                    }),
                );
            },
            onSettled: () => {
                queryClient.invalidateQueries(PENDING_TIMESHEETS);
            },
        },
    );

    return { mutate, isApproveTimesheetingsLoading };
};
